/**
 * Staff and access, the ordering admin surface (ORDR-94, closes FR-SUR-014).
 *
 * Before this, no surface anywhere granted an ordering role. FRS-33 recorded
 * "Staff and grants | SHIPPED | section 3", which was wrong: section 3
 * catalogues the capability bundles, and a catalogue of roles is not a surface
 * that grants them. That row is why the work was never costed.
 *
 * COLLAPSED BY DEFAULT, AND THAT IS NOT A STYLE CHOICE. The framework's admin
 * route renders host `children` at the BOTTOM, below whichever panel is open,
 * rather than as an entry in its surface chooser. Its docstring says "ONE
 * SURFACE AT A TIME. Five panels stacked in a column is a scroll, not a
 * screen." A panel that opened itself would make that sentence false. Closed,
 * it costs one line until somebody asks for it.
 *
 * THE PICKER RUNS THE SERVER'S RULE, which is the whole point of the surface
 * existing separately from paio-ui's. See `grantPolicy.ts`: with AUTH-978's
 * catalogue it offers every role whose capabilities are a SUBSET of the
 * caller's at the chosen scope, so a brand-admin can finally hand a cook
 * `kitchen-display`. It is still only a pre-filter; the server decides every
 * write, exactly as this repo's CLAUDE.md requires.
 *
 * IT FAILS CLOSED. If the catalogue cannot be read the form does not appear at
 * all, and says so. Falling back to "the roles you hold" would be the paio-ui
 * behaviour this surface exists to replace, and would look identical to
 * working.
 *
 * THE ROSTER NAMES PEOPLE AND THEIR LAST SIGN-IN (ORDR-120). It reads
 * `GET /access/roster`, never the scope-named grants read it replaced, and
 * revoke and restore are each one click on a button naming who and what. See
 * `Roster.tsx`, `signIn.ts` and `RemovedThisVisit.tsx`.
 *
 * NO STATE IS SET SYNCHRONOUSLY INSIDE AN EFFECT. Both reads set state only
 * once their answer arrives, and the roster is keyed by the scope it was read
 * for, so "loading" is DERIVED (no answer for this scope yet) rather than set
 * before the request. That also means a slow answer for a scope the manager
 * has already left can never be drawn under the new one.
 */

import { useCallback, useEffect, useMemo, useState, type ReactElement } from "react";
import { accessApi as defaultApi, ORDERING_APP, type AccessApi } from "./accessApi.js";
import { GrantForm } from "./GrantForm.js";
import { grantableRoles, manageableScopes, scopeKey } from "./grantPolicy.js";
import { RemovedThisVisit, type RemovedGrant } from "./RemovedThisVisit.js";
import { Roster, type RosterRead } from "./Roster.js";
import { holderName, roleName } from "./rosterLabels.js";
import type { CatalogRole, Grant, RosterEntry } from "./types.js";

export interface GrantsPanelProps {
  /** The signed-in user, whose own grants decide what may be conferred. */
  readonly ownUserId: string;
  /** Injected in tests. */
  readonly api?: AccessApi;
}

type Status = "loading" | "ready" | "error";

export function GrantsPanel({ ownUserId, api = defaultApi }: GrantsPanelProps): ReactElement {
  const [status, setStatus] = useState<Status>("loading");
  // MY grants: what I may confer and where. Never the roster.
  const [grants, setGrants] = useState<readonly Grant[]>([]);
  // EVERY grant at the selected scope, with its holder. A separate read.
  const [roster, setRoster] = useState<RosterRead | undefined>(undefined);
  // What THIS page revoked, so it can be restored (page memory only).
  const [removed, setRemoved] = useState<readonly RemovedGrant[]>([]);
  const [catalog, setCatalog] = useState<readonly CatalogRole[]>([]);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const [userId, setUserId] = useState("");
  const [scopeIndex, setScopeIndex] = useState(0);
  const [roleKey, setRoleKey] = useState("");

  const load = useCallback(
    (): Promise<void> =>
      // Both, or neither. A grants list without a catalogue cannot filter, and
      // this panel must not render a picker it cannot trust.
      Promise.all([api.listGrants(ORDERING_APP), api.listCatalog(ORDERING_APP)]).then(
        ([nextGrants, nextCatalog]) => {
          setGrants(nextGrants);
          setCatalog(nextCatalog);
          setStatus("ready");
        },
        (caught: unknown) => {
          setMessage(caught instanceof Error ? caught.message : "Could not load access.");
          setStatus("error");
        },
      ),
    [api],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const scopes = useMemo(
    () => manageableScopes(grants, ownUserId, catalog),
    [grants, ownUserId, catalog],
  );
  const scope = scopes[scopeIndex] ?? scopes[0];
  const roles = useMemo(
    () => (scope === undefined ? [] : grantableRoles(grants, ownUserId, catalog, scope)),
    [grants, ownUserId, catalog, scope],
  );
  const role = roles.some((r) => r.key === roleKey) ? roleKey : (roles[0]?.key ?? "");

  /**
   * The roster: every grant at the SELECTED scope, which needs a second read.
   *
   * ORDR-120: that read is `listRoster`, which carries each holder's name and
   * last sign-in. It never falls back to the grants read, which carries
   * neither: a fallback would look like working with the sign-in column gone.
   *
   * `listGrants(app)` names only the app, and paio-auth answers that with My
   * Access, the caller's own grants alone. Naming the concrete scope is what
   * asks the org-admin question. Until ORDR-103 this panel rendered the
   * scope-discovery read as the roster, so it showed you only yourself: a grant
   * you made was invisible and another person's could never be revoked.
   *
   * Only scopes from `manageableScopes` are ever named, because `list_grants`
   * raises rather than downgrading, so asking about an unmanaged scope is a 403
   * of the client's own making.
   */
  const loadRoster = useCallback(async (): Promise<void> => {
    if (scope === undefined) {
      return;
    }
    const key = scopeKey(scope);
    await api.listRoster(ORDERING_APP, scope).then(
      (entries) => setRoster({ key, entries, at: new Date() }),
      // A failed read, SAID to be one. Never an empty list, never a fallback.
      () => setRoster({ key, entries: null, at: new Date() }),
    );
  }, [api, scope]);

  useEffect(() => {
    void loadRoster();
  }, [loadRoster]);

  const currentKey = scope === undefined ? "" : scopeKey(scope);
  const rosterHere = roster?.key === currentKey ? roster : undefined;

  const submit = useCallback(async (): Promise<void> => {
    if (scope === undefined || role === "" || userId.trim() === "") {
      return;
    }
    setBusy(true);
    setMessage("");
    try {
      await api.createGrant({
        user_id: userId.trim(),
        app: ORDERING_APP,
        role_key: role,
        scope_type: scope.scopeType,
        scope_id: scope.scopeId,
      });
      setUserId("");
      await Promise.all([load(), loadRoster()]);
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "Could not grant that role.");
    } finally {
      setBusy(false);
    }
  }, [api, load, loadRoster, role, scope, userId]);

  /** One click, no dialog: the button already named who and what (FR-STF-004). */
  const revoke = useCallback(
    async (entry: RosterEntry): Promise<void> => {
      const { grant } = entry;
      setBusy(true);
      setMessage("");
      try {
        await api.revokeGrant(grant.id);
        const item: RemovedGrant = {
          grantId: grant.id,
          user_id: grant.user_id,
          holderName: holderName(entry, ownUserId),
          roleName: roleName(grant.role_key, catalog),
          role_key: grant.role_key,
          scope_type: grant.scope_type,
          scope_id: grant.scope_id,
        };
        setRemoved((prior) => [...prior, item]);
        await Promise.all([load(), loadRoster()]);
      } catch (caught) {
        // The row stays: nothing is reloaded, so nothing disappears.
        setMessage(caught instanceof Error ? caught.message : "Could not revoke that grant.");
      } finally {
        setBusy(false);
      }
    },
    [api, catalog, load, loadRoster, ownUserId],
  );

  /** The same five fields the revoke removed, and never `expires_at` (FR-STF-002). */
  const restore = useCallback(
    async (item: RemovedGrant): Promise<void> => {
      setBusy(true);
      setMessage("");
      try {
        await api.createGrant({
          user_id: item.user_id,
          app: ORDERING_APP,
          role_key: item.role_key,
          scope_type: item.scope_type,
          scope_id: item.scope_id,
        });
        setRemoved((prior) => prior.filter((other) => other.grantId !== item.grantId));
        await Promise.all([load(), loadRoster()]);
      } catch (caught) {
        // The entry stays, so the manager can try again.
        setMessage(caught instanceof Error ? caught.message : "Could not restore that access.");
      } finally {
        setBusy(false);
      }
    },
    [api, load, loadRoster],
  );

  const removedHere = removed.filter(
    (item) => scopeKey({ scopeType: item.scope_type, scopeId: item.scope_id }) === currentKey,
  );

  return (
    <details data-testid="grants-panel" className="mt-6 rounded-[var(--border-radius-md)] border border-[var(--color-neutral-300)]">
      <summary className="cursor-pointer p-3 text-sm font-medium">Staff and access</summary>

      <div className="border-t border-[var(--color-neutral-200)] p-3">
        {status === "loading" && <p className="text-sm">Loading access...</p>}

        {status === "error" && (
          <p role="alert" data-testid="grants-error" className="text-sm">
            {message} Nobody can be granted a role until this loads.
          </p>
        )}

        {status === "ready" && scopes.length === 0 && (
          <p data-testid="grants-no-authority" className="text-sm">
            You do not manage access at any venue, so you cannot grant roles here.
          </p>
        )}

        {status === "ready" && scopes.length > 0 && (
          <>
            <GrantForm
              userId={userId}
              scopes={scopes}
              scopeIndex={scopeIndex}
              roles={roles}
              role={role}
              busy={busy}
              onUserIdChange={setUserId}
              onScopeIndexChange={setScopeIndex}
              onRoleChange={setRoleKey}
              onSubmit={() => void submit()}
            />

            {message !== "" && (
              <p role="alert" data-testid="grant-message" className="mb-3 text-sm">
                {message}
              </p>
            )}

            <Roster
              read={rosterHere}
              ownUserId={ownUserId}
              catalog={catalog}
              busy={busy}
              onRevoke={(entry) => void revoke(entry)}
            />

            <RemovedThisVisit
              removed={removedHere}
              ownUserId={ownUserId}
              busy={busy}
              onRestore={(item) => void restore(item)}
            />
          </>
        )}
      </div>
    </details>
  );
}
